import { useState, useEffect, useCallback } from 'react';
import { BaseFormModal } from '@/components/BaseFormModal';
import { Dropdownlist } from '@/components/Dropdownlist';
import { api } from '@/services/api-class';
import { App } from '@/services/app-compat';
import { NOC_PRIORITY_OPTIONS } from '@/constants/priorities';
import type { NatureOfCallData } from '@/services/api-types';

const PRIORITY_ITEMS = NOC_PRIORITY_OPTIONS.map((opt) => ({
  label: opt.label,
  value: opt.value === null ? '__none__' : opt.value,
}));

export interface EditPriorityModalProps {
  open: boolean;
  noc: NatureOfCallData | null;
  theme?: string;
  onCancel: () => void;
  onSaved: () => void;
}

export function EditPriorityModal({
  open,
  noc,
  theme,
  onCancel,
  onSaved,
}: EditPriorityModalProps) {
  const [priority, setPriority] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setPriority(noc?.priority ?? null);
      setSaving(false);
    }
  }, [open, noc]);

  const selectValue = priority === null ? '__none__' : priority;

  const handleChange = useCallback((val: string | number | null) => {
    if (val === '__none__' || val === null) {
      setPriority(null);
    } else {
      setPriority(Number(val));
    }
  }, []);

  const handleSave = useCallback(() => {
    if (!noc) return;
    setSaving(true);
    api.updateNatureOfCallPriority(
      noc.code,
      priority,
      () => {
        setSaving(false);
        onSaved();
      },
      () => {
        setSaving(false);
        App.showPopupNotification('Failed to update nature of call priority.', 'error');
      }
    );
  }, [noc, priority, onSaved]);

  const subheader = noc ? (
    <div className="kstc-sop-header">
      <span className="kstc-sop-noc-label">{noc.value}</span>
    </div>
  ) : null;

  return (
    <BaseFormModal
      open={open}
      title="Edit Nature of Call Priority"
      theme={theme}
      dialogClass="kstc-dialog--form"
      subheader={subheader}
      saveText={saving ? 'Saving...' : 'Save'}
      saveDisabled={saving}
      busy={saving}
      hint="Higher-priority NOCs can raise the Call Card priority when attached."
      onSave={handleSave}
      onCancel={onCancel}
    >
      <div className="kstc-form-group">
        <label className="kstc-label">Priority</label>
        <Dropdownlist
          value={selectValue as any}
          items={PRIORITY_ITEMS}
          placeholder="Select priority..."
          sortItems={false}
          searchable={false}
          onChange={handleChange}
        />
      </div>
    </BaseFormModal>
  );
}

(window as any).EditPriorityModalReact = EditPriorityModal;
